import setLocalStorageApi from 'api/browser/localstorage/set';
import afterLogin from 'AppRoutes/utils';
import { LOCALSTORAGE } from 'constants/localStorage';
import ROUTES from 'constants/routes';
import { isValidTheme, THEME_UPDATE_EVENT } from 'hooks/useDarkMode/constant';
import history from 'lib/history';
import { getIframeAllowedOrigins } from 'utils/envConfig';

interface JWTPayload {
	accessJwt: string;
	refreshJwt: string;
	theme: string;
	nonce: string;
	timestamp: number;
	counter: number;
	targetPath?: string;
}

interface ProcessedMessage {
	nonce: string;
	counter: number;
	timestamp: number;
	origin: string;
}

interface AuthenticationConfig {
	allowedOrigins: string[];
	maxMessageAge: number;
	requiredFields: string[];
}

const AUTH_CONFIG: AuthenticationConfig = {
	allowedOrigins: getIframeAllowedOrigins(),
	maxMessageAge: 3 * 60 * 1000,
	requiredFields: ['accessJwt', 'refreshJwt', 'nonce', 'timestamp', 'counter'],
};

const processedMessages = new Map<string, ProcessedMessage>();
const MESSAGE_HISTORY_SIZE = 100;
const MESSAGE_TYPE_AUTH_STATUS = 'O11Y_AUTH_STATUS';
const INVALID_PAYLOAD_ERROR = 'Invalid payload structure';
const INVALID_NONCE_ERROR = 'Invalid or reused nonce';
const AUTH_PROCESSING_ERROR = 'Authentication processing failed';
const INVALID_COUNTER_ERROR = 'Invalid message counter';
const INVALID_TIMESTAMP_ERROR = 'Invalid message timestamp';
const INVALID_COUNTER_FORMAT_ERROR = 'Invalid counter format';
const FUTURE_TIMESTAMP_THRESHOLD_MS = -100;
const MAX_COUNTER_INCREMENT = 1;
const COUNTER_RESET_THRESHOLD_MS = 30 * 60 * 1000;
const MIN_NONCE_LENGTH = 16;

const countersByOrigin = new Map<string, number>();

const isValidNavigationPath = (path: string | undefined): boolean => {
	if (!path || typeof path !== 'string') {
		return false;
	}

	const cleanPath = path.split('?')[0].split('#')[0];

	if (!cleanPath.startsWith('/')) {
		return false;
	}

	const validPaths = Object.values(ROUTES);
	return validPaths.some((route) => {
		if (route.includes(':')) {
			const routePattern = route.replace(/:[^/]+/g, '[^/]+');
			const regex = new RegExp(`^${routePattern}$`);
			return regex.test(cleanPath);
		}
		return route === cleanPath;
	});
};

const extractTargetPath = (payload: JWTPayload): string => {
	if (payload.targetPath && isValidNavigationPath(payload.targetPath)) {
		const [pathAndQuery] = payload.targetPath.split('#');
		return pathAndQuery;
	}

	const currentPath = window.location.pathname;
	if (
		currentPath &&
		currentPath !== '/' &&
		currentPath !== ROUTES.LOGIN &&
		isValidNavigationPath(currentPath)
	) {
		return currentPath;
	}

	return ROUTES.APPLICATION;
};

const cleanupOldMessages = (): void => {
	const now = Date.now();
	const expired: string[] = [];

	processedMessages.forEach((message, key) => {
		if (now - message.timestamp > AUTH_CONFIG.maxMessageAge) {
			expired.push(key);
		}
	});

	expired.forEach((key) => processedMessages.delete(key));

	if (processedMessages.size > MESSAGE_HISTORY_SIZE) {
		const sortedEntries = Array.from(processedMessages.entries()).sort(
			(a, b) => a[1].timestamp - b[1].timestamp,
		);

		const toRemove = sortedEntries.slice(
			0,
			sortedEntries.length - MESSAGE_HISTORY_SIZE,
		);
		toRemove.forEach(([key]) => processedMessages.delete(key));
	}
};

const isValidOrigin = (origin: string): boolean => {
	if (AUTH_CONFIG.allowedOrigins.length === 0) {
		const devOriginPattern =
			/^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0):(3000|3001|8080)$|^https?:\/\/(gdk|gitlab)\.test:(3000|3001|8080)$/;
		return devOriginPattern.test(origin);
	}

	const isValid = AUTH_CONFIG.allowedOrigins.includes(origin);
	if (!isValid) {
		console.warn('Message from untrusted origin:', origin);
	}
	return isValid;
};

const isValidTimestamp = (timestamp: number): boolean => {
	const now = Date.now();
	const messageAge = now - timestamp;

	if (messageAge < FUTURE_TIMESTAMP_THRESHOLD_MS) {
		console.warn('Message timestamp is in the future');
		return false;
	}

	if (messageAge > AUTH_CONFIG.maxMessageAge) {
		console.warn('Message timestamp is too old');
		return false;
	}

	return true;
};

const isValidNonce = (nonce: string): boolean => {
	if (
		!nonce ||
		typeof nonce !== 'string' ||
		nonce.length < MIN_NONCE_LENGTH ||
		!new RegExp(`^[A-Za-z0-9\\-_]{${MIN_NONCE_LENGTH},}$`).test(nonce)
	) {
		console.warn('Invalid nonce format');
		return false;
	}

	const hasDuplicateNonce = Array.from(processedMessages.values()).some(
		(message) => message.nonce === nonce,
	);

	if (hasDuplicateNonce) {
		console.warn('Nonce already processed (replay attack?)');
		return false;
	}

	return true;
};

const validateCounterFormat = (
	counter: number,
): { valid: boolean; reason?: string } => {
	if (typeof counter !== 'number' || counter < 1) {
		console.warn(INVALID_COUNTER_FORMAT_ERROR);
		return { valid: false, reason: INVALID_COUNTER_FORMAT_ERROR };
	}
	return { valid: true };
};

const checkDuplicateMessage = (
	messageKey: string,
): { valid: boolean; reason?: string } => {
	if (processedMessages.has(messageKey)) {
		console.warn('Duplicate message detected (same counter + nonce)');
		return { valid: false, reason: 'Duplicate message detected' };
	}
	return { valid: true };
};

const getLastMessageFromOrigin = (
	origin: string,
): ProcessedMessage | undefined =>
	Array.from(processedMessages.values())
		.filter((m) => m.origin === origin)
		.sort((a, b) => b.timestamp - a.timestamp)[0];

const shouldResetCounter = (
	lastMessage: ProcessedMessage | undefined,
): boolean =>
	lastMessage !== undefined &&
	Date.now() - lastMessage.timestamp > COUNTER_RESET_THRESHOLD_MS;

const validateCounterIncrement = (
	counter: number,
	lastCounter: number,
	origin: string,
): { valid: boolean; reason?: string } => {
	if (counter <= lastCounter) {
		console.warn('Counter not incrementing:', {
			received: counter,
			lastCounter,
			origin,
		});
		return { valid: false, reason: 'Counter not incrementing' };
	}

	if (counter > lastCounter + MAX_COUNTER_INCREMENT) {
		console.warn('Counter jump too large:', {
			received: counter,
			lastCounter,
		});
		return { valid: false, reason: 'Counter jump too large' };
	}

	return { valid: true };
};

const isValidCounter = (
	counter: number,
	origin: string,
	nonce: string,
): { valid: boolean; reason?: string } => {
	const formatCheck = validateCounterFormat(counter);
	if (!formatCheck.valid) {
		return formatCheck;
	}

	const messageKey = `${origin}-${nonce}-${counter}`;
	const duplicateCheck = checkDuplicateMessage(messageKey);
	if (!duplicateCheck.valid) {
		return duplicateCheck;
	}

	const lastCounter = countersByOrigin.get(origin) || 0;
	const lastMessage = getLastMessageFromOrigin(origin);

	if (shouldResetCounter(lastMessage)) {
		countersByOrigin.set(origin, 0);
		return { valid: true };
	}

	return validateCounterIncrement(counter, lastCounter, origin);
};

const validatePayload = (payload: unknown): payload is JWTPayload => {
	if (!payload || typeof payload !== 'object') {
		return false;
	}

	const obj = payload as Record<string, unknown>;

	const missingFields = AUTH_CONFIG.requiredFields.filter(
		(field) => !(field in obj),
	);
	if (missingFields.length > 0) {
		console.warn('Missing required fields in payload:', missingFields);
		return false;
	}

	if (
		typeof obj.accessJwt !== 'string' ||
		typeof obj.refreshJwt !== 'string' ||
		typeof obj.nonce !== 'string' ||
		typeof obj.timestamp !== 'number' ||
		typeof obj.counter !== 'number'
	) {
		console.warn('Invalid field types in payload');
		return false;
	}

	if (obj.targetPath !== undefined && typeof obj.targetPath !== 'string') {
		console.warn('Invalid targetPath type in payload');
		return false;
	}

	return true;
};

const processJwtLogin = (
	payload: JWTPayload,
	messageSource: MessageEventSource | null,
	origin: string,
): void => {
	const { accessJwt, refreshJwt, theme, nonce, timestamp, counter } = payload;

	const messageKey = `${origin}-${nonce}-${counter}`;
	processedMessages.set(messageKey, {
		nonce,
		counter,
		timestamp,
		origin,
	});

	countersByOrigin.set(origin, counter);

	cleanupOldMessages();

	afterLogin(accessJwt, refreshJwt);

	setLocalStorageApi(LOCALSTORAGE.IS_LOGGED_IN, 'true');

	if (isValidTheme(theme)) {
		setLocalStorageApi(LOCALSTORAGE.THEME, theme);
		window.dispatchEvent(
			new CustomEvent(THEME_UPDATE_EVENT, {
				detail: { theme },
			}),
		);
	}

	if (messageSource && 'postMessage' in messageSource) {
		const parentOrigin = origin;
		if (isValidOrigin(parentOrigin)) {
			(messageSource as Window).postMessage(
				{
					type: MESSAGE_TYPE_AUTH_STATUS,
					authenticated: true,
					timestamp: Date.now(),
					nonce,
					counter,
				},
				parentOrigin,
			);
		} else {
			console.error('Cannot send response: invalid parent origin');
		}
	}

	const targetPath = extractTargetPath(payload);
	history.push(targetPath);
};

const handleAuthError = (
	event: MessageEvent,
	payload: JWTPayload | null,
	error: string,
): void => {
	if (event.source && 'postMessage' in event.source) {
		const parentOrigin = event.origin;
		if (isValidOrigin(parentOrigin)) {
			(event.source as Window).postMessage(
				{
					type: MESSAGE_TYPE_AUTH_STATUS,
					authenticated: false,
					error,
					timestamp: Date.now(),
					nonce: error === INVALID_TIMESTAMP_ERROR ? null : payload?.nonce || null,
					counter:
						error === INVALID_TIMESTAMP_ERROR ? null : payload?.counter || null,
				},
				parentOrigin,
			);
		}
	}
};

const isValidMessageType = (data: unknown): boolean => {
	if (!data || typeof data !== 'object') {
		return false;
	}

	const messageData = data as Record<string, unknown>;
	const { type } = messageData;

	return typeof type === 'string' && type.startsWith('O11Y_JWT_LOGIN');
};

const processPayload = async (rawPayload: unknown): Promise<JWTPayload> => {
	if (!validatePayload(rawPayload)) {
		throw new Error(INVALID_PAYLOAD_ERROR);
	}

	return rawPayload as JWTPayload;
};

const validatePayloadChecks = async (
	payload: JWTPayload,
	origin: string,
): Promise<{ valid: boolean; error?: string }> => {
	if (!isValidTimestamp(payload.timestamp)) {
		return { valid: false, error: INVALID_TIMESTAMP_ERROR };
	}

	if (!isValidNonce(payload.nonce)) {
		return { valid: false, error: INVALID_NONCE_ERROR };
	}

	const counterValidation = isValidCounter(
		payload.counter,
		origin,
		payload.nonce,
	);

	if (!counterValidation.valid) {
		return {
			valid: false,
			error: INVALID_COUNTER_ERROR,
		};
	}

	return { valid: true };
};

const processAuthPayload = async (rawPayload: unknown): Promise<JWTPayload> => {
	try {
		return await processPayload(rawPayload);
	} catch (error) {
		console.error('Payload processing failed:', error);
		throw new Error(error instanceof Error ? error.message : 'Processing failed');
	}
};

const validateMessageOrigin = (
	origin: string,
): { valid: boolean; error?: string } => {
	if (!isValidOrigin(origin)) {
		console.warn('Message from untrusted origin:', origin);
		return { valid: false, error: 'Invalid origin' };
	}
	return { valid: true };
};

const validateMessageFormat = (
	data: unknown,
): { valid: boolean; error?: string } => {
	if (!isValidMessageType(data)) {
		return { valid: false, error: 'Invalid message format' };
	}
	return { valid: true };
};

const validateMessage = (
	event: MessageEvent,
): { valid: boolean; error?: string } => {
	const originValidation = validateMessageOrigin(event.origin);
	if (!originValidation.valid) {
		return { valid: false, error: originValidation.error };
	}

	const formatValidation = validateMessageFormat(event.data);
	if (!formatValidation.valid) {
		return { valid: false, error: formatValidation.error };
	}

	return { valid: true };
};

const validateAuthMessage = async (
	event: MessageEvent,
): Promise<{ valid: boolean; payload?: JWTPayload; error?: string }> => {
	const messageValidation = validateMessage(event);
	if (!messageValidation.valid) {
		return { valid: false, error: messageValidation.error };
	}

	const { payload: rawPayload, parentOrigin } = event.data;

	const actualParentOrigin = parentOrigin || event.origin;

	try {
		const payload = await processAuthPayload(rawPayload);
		const validation = await validatePayloadChecks(payload, actualParentOrigin);

		if (!validation.valid) {
			return { valid: false, error: validation.error, payload };
		}

		return { valid: true, payload };
	} catch (error) {
		return {
			valid: false,
			error: error instanceof Error ? error.message : 'Processing failed',
		};
	}
};

const handleValidationResult = (
	event: MessageEvent,
	validation: { valid: boolean; payload?: JWTPayload; error?: string },
): boolean => {
	if (!validation.valid) {
		if (validation.payload || validation.error) {
			handleAuthError(
				event,
				validation.payload || null,
				validation.error || AUTH_PROCESSING_ERROR,
			);
		}
		return false;
	}

	if (!validation.payload) {
		console.error('Missing payload in valid validation result');
		handleAuthError(event, null, AUTH_PROCESSING_ERROR);
		return false;
	}

	return true;
};

let currentMessageHandler: ((event: MessageEvent) => void) | null = null;

export const cleanupEventListeners = (): void => {
	if (currentMessageHandler) {
		window.removeEventListener('message', currentMessageHandler);
		currentMessageHandler = null;
	}
};

export const checkForJwtAuthentication = (): void => {
	if (window !== window.top) {
		cleanupEventListeners();

		const messageHandler = async (event: MessageEvent): Promise<void> => {
			const validation = await validateAuthMessage(event);

			if (!handleValidationResult(event, validation)) {
				return;
			}

			try {
				if (validation.payload) {
					processJwtLogin(validation.payload, event.source, event.origin);
				} else {
					console.error('Missing payload in valid validation result');
					handleAuthError(event, null, AUTH_PROCESSING_ERROR);
				}
			} catch (error) {
				console.error('Error processing JWT login:', error);
				handleAuthError(event, validation.payload || null, AUTH_PROCESSING_ERROR);
			}
		};

		currentMessageHandler = messageHandler;
		window.addEventListener('message', messageHandler);
	}
};

export const initJwtAuthentication = (): (() => void) => {
	checkForJwtAuthentication();

	return history.listen(() => {
		checkForJwtAuthentication();
	});
};

export const updateAllowedOrigins = (origins: string[]): void => {
	AUTH_CONFIG.allowedOrigins = origins;
};

export const resetAuthConfig = (): void => {
	AUTH_CONFIG.allowedOrigins = getIframeAllowedOrigins();
	AUTH_CONFIG.maxMessageAge = 3 * 60 * 1000;
	AUTH_CONFIG.requiredFields = [
		'accessJwt',
		'refreshJwt',
		'nonce',
		'timestamp',
		'counter',
	];
};

export const resetMessageCounters = (): void => {
	processedMessages.clear();
	countersByOrigin.clear();
};

export const getCounterState = (): {
	countersByOrigin: Record<string, number>;
	processedMessagesCount: number;
	oldestMessage: ProcessedMessage | undefined;
} => ({
	countersByOrigin: Object.fromEntries(countersByOrigin),
	processedMessagesCount: processedMessages.size,
	oldestMessage: Array.from(processedMessages.values()).sort(
		(a, b) => a.timestamp - b.timestamp,
	)[0],
});

export const getAuthConfig = (): {
	allowedOrigins: string[];
	maxMessageAge: number;
	requiredFields: string[];
} => ({
	allowedOrigins: [...AUTH_CONFIG.allowedOrigins],
	maxMessageAge: AUTH_CONFIG.maxMessageAge,
	requiredFields: [...AUTH_CONFIG.requiredFields],
});
